import { z } from "zod";

export const JiraIssueBadge = z.object({
  id: z.number().int(),
  status: z.string(),
  issue_key: z.string().default(""),
  issue_url: z.string().default(""),
  step_id: z.number().int().nullish(),
  summary: z.string().default(""),
  created_at: z.string(),
});

const JiraAttachment = z.object({
  file_id: z.number().int().nullish(),
  filename: z.string().default(""),
  category: z.string().default(""),
  role: z.string().default(""),
  size: z.number().nullish(),
  download_url: z.string().default(""),
  selected: z.boolean().default(false),
  reason: z.string().default(""),
  result: z.string().default(""),
});

const JiraRequiredField = z.object({
  key: z.string(),
  name: z.string().default(""),
  type: z.string().default(""),
  multiple: z.boolean().default(false),
  has_default: z.boolean().default(false),
  covered: z.boolean().default(false),
  allowed_values: z.array(z.object({ id: z.string(), label: z.string().default("") })).default([]),
  example: z.record(z.unknown()).optional(),
});

/** 422 body of `POST /jira-issues/`: fields the board refused to create the issue without. */
export const JiraFieldsRequired = z.object({
  message: z.string(),
  code: z.literal("jira_field_required"),
  fields: z.array(z.string()).nullish(),
  required_fields: z.array(JiraRequiredField),
});

const JiraFormField = JiraRequiredField.extend({
  required: z.boolean().default(false),
  source: z.string().default("tapioca"),
  send_as: z.string().default(""),
  value: z.unknown().optional(),
});

export const JiraIssue = z.object({
  id: z.number().int(),
  cycle_id: z.number().int(),
  step_id: z.number().int().nullish(),
  status: z.string(),
  mode: z.string(),
  issue_key: z.string().default(""),
  issue_url: z.string().default(""),
  project_key: z.string().default(""),
  summary: z.string().default(""),
  issue_type: z.string().default(""),
  priority: z.string().default(""),
  labels: z.array(z.string()).default([]),
  error: z.string().default(""),
  attachments: z.array(JiraAttachment).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});

export const JiraDraft = z.object({
  cycle_id: z.number().int(),
  step_id: z.number().int().nullish(),
  project_id: z.number().int().nullish(),
  can_create: z.boolean(),
  warnings: z.array(z.string()).default([]),
  summary: z.string(),
  description: z.string(),
  issue_type: z.string().default(""),
  priority: z.string().default(""),
  labels: z.array(z.string()).default([]),
  expected: z.string().default(""),
  actual: z.string().default(""),
  state: z.string().default(""),
  phase: z.string().default(""),
  processing_type: z.string().default(""),
  error_type: z.string().default(""),
  error_category: z.string().default(""),
  worker_name: z.string().default(""),
  worker_type: z.string().default(""),
  execution_start: z.string().nullish(),
  step_finished_at: z.string().nullish(),
  case_name: z.string().default(""),
  case_key: z.string().default(""),
  log_lines: z.array(z.string()).default([]),
  links: z.record(z.string()).default({}),
  attachments: z.array(JiraAttachment).default([]),
  required_fields: z.array(JiraRequiredField).default([]),
  form: z.array(JiraFormField).default([]),
  fields_status: z.string().default("ok"),
  fingerprint: z.string().default(""),
  fingerprint_label: z.string().default(""),
  jira_project_key: z.string().default(""),
  needs_project: z.boolean().default(false),
  jira_base_url: z.string().default(""),
  duplicates: z
    .array(
      z.object({
        issue_key: z.string(),
        issue_url: z.string(),
        summary: z.string().default(""),
        status: z.string().default(""),
        status_category: z.string().default(""),
        issue_type: z.string().default(""),
        source: z.string().default("jira"),
        cycle_id: z.number().int().nullish(),
      }),
    )
    .default([]),
  existing: JiraIssue.nullish(),
});

export const JiraIssueCreate = z.object({
  cycle_id: z.number().int().positive(),
  step_id: z.number().int().positive().optional(),
  summary: z.string().default(""),
  note: z.string().default(""),
  issue_type: z.string().default(""),
  priority: z.string().default(""),
  labels: z.array(z.string()).nullish(),
  file_ids: z.array(z.number().int()).nullish(),
  include_video: z.boolean().nullish(),
  extra_fields: z.record(z.unknown()).nullish(),
  force: z.boolean().default(false),
  link_to_issue_key: z.string().default(""),
  project_key: z.string().default(""),
});

export type JiraDraftValue = z.infer<typeof JiraDraft>;
export type JiraRequiredFieldValue = z.infer<typeof JiraRequiredField>;
export type JiraIssueValue = z.infer<typeof JiraIssue>;
export type JiraIssueCreateValue = z.input<typeof JiraIssueCreate>;
